import { arrayMove } from '@dnd-kit/sortable';
import { Box, Stack, Typography } from '@mui/material';
import { cloneElement, useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { useFormFieldsDisabled } from '@/components/form/context/formFieldsDisabled.context';
import { FormAwareRadioGroup } from '@/components/form/formAwareInputs/FormAwareRadioGroup';
import { DraggableListItem } from '@/components/lists/DragAndDropList/DraggableListItem';
import { SortableList } from '@/components/lists/DragAndDropList/SortableList';
import { TableCell } from '@/components/lists/TableCell';
import { useFormContext } from '@/components/react-hook-form';
import { COLORS } from '@/styles/tokens/colors';
import {
  IncomeTaxPaymentConfigGrantorPaymentMethod,
  IncomeTaxPaymentConfigNonGrantorPaymentMethod,
} from '@/types/schema';

import { GrowthProfileModalFormShape } from '../GrowthProfileModal.types';
import { TaxDragEntity } from './TaxDrag.types';
import { applySavedTaxPayerOrder, isEligibleTaxPayer } from './TaxDrag.utils';

interface TaxPayerSettingsProps {
  entities: TaxDragEntity[];
}

export function TaxPayerSettings({ entities }: TaxPayerSettingsProps) {
  const { disabled } = useFormFieldsDisabled();
  const { control, setValue } = useFormContext<GrowthProfileModalFormShape>();
  const [taxDrag] = useWatch({ control, name: ['taxDrag'] });

  useEffect(() => {
    if (disabled) return;
    // Remove only confirmed ineligible sources. Entities may still be loading.
    const ineligibleIds = new Set(
      entities
        .filter((entity) => !isEligibleTaxPayer(entity))
        .map(({ id }) => id)
    );
    const customPayerOrder = taxDrag.customPayerOrder.filter(
      (id) => !ineligibleIds.has(id)
    );
    if (customPayerOrder.length !== taxDrag.customPayerOrder.length) {
      setValue('taxDrag.customPayerOrder', customPayerOrder, {
        shouldDirty: true,
      });
    }
  }, [disabled, entities, setValue, taxDrag.customPayerOrder]);

  const payerEntities = useMemo(
    () => applySavedTaxPayerOrder(entities, taxDrag.customPayerOrder),
    [entities, taxDrag.customPayerOrder]
  );

  return (
    <Stack spacing={3}>
      <Stack spacing={0.5}>
        <Typography variant="title-m">Tax paying entity</Typography>
        <Typography variant="body-xs" sx={{ color: COLORS.TEXT.PRIMARY }}>
          The entities that pay the taxes on investment income their assets
          generate. Use the settings below to control how that liability is
          allocated across entities.
        </Typography>
      </Stack>

      <Stack spacing={1.5}>
        <Stack spacing={0.5}>
          <Typography variant="title-s">
            Entities taxed to the grantor
          </Typography>
          <Typography variant="body-xs" sx={{ color: COLORS.TEXT.PRIMARY }}>
            Specify which entities pay the taxes on investment income generated
            by grantor trusts and directly held entities.
          </Typography>
        </Stack>
        <FormAwareRadioGroup<GrowthProfileModalFormShape>
          control={control}
          variant="card"
          size="small"
          fieldName="taxDrag.grantorPaymentMethod"
          row
          options={[
            {
              label: 'Custom selection',
              value: IncomeTaxPaymentConfigGrantorPaymentMethod.Custom,
              helpText:
                'Specify the order in which entities are depleted to pay the tax.',
            },
            {
              label: 'Pro rata',
              value: IncomeTaxPaymentConfigGrantorPaymentMethod.ProRata,
              helpText:
                'Eligible revocable trusts, joint accounts, and individual accounts pay shares proportional to their available value. Payers must be grantor-taxed and in both federal and state estates.',
            },
          ]}
        />
        {taxDrag.grantorPaymentMethod ===
          IncomeTaxPaymentConfigGrantorPaymentMethod.Custom && (
          <Stack spacing={1}>
            <Typography variant="title-xs">Depletion order</Typography>
            <Box
              sx={{
                border: `1px solid ${COLORS.GRAY[200]}`,
                borderRadius: 1,
                px: 1,
                bgcolor: 'background.paper',
                '& .MuiTypography-root': { fontWeight: 400 },
                '& [aria-roledescription="sortable"]': {
                  cursor: 'grab',
                  '&:active': { cursor: 'grabbing' },
                },
              }}
            >
              <SortableList
                items={payerEntities}
                getId={({ id }) => id}
                disabled={disabled}
                onReorder={(from, to) =>
                  setValue(
                    'taxDrag.customPayerOrder',
                    arrayMove(payerEntities, from, to).map(({ id }) => id),
                    { shouldDirty: true }
                  )
                }
              >
                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                  {payerEntities.map((payer, index) => (
                    <DraggableListItem
                      key={payer.id}
                      item={payer}
                      isLast={index === payerEntities.length - 1}
                      hasDragHandle
                      withDividers
                      disabled={disabled}
                      ItemInnerComponent={({ item, Draghandle }) => (
                        <TableCell
                          heading={item.displayName}
                          Draghandle={cloneElement(Draghandle, {
                            'aria-label': `Reorder ${item.displayName}`,
                          })}
                        />
                      )}
                    />
                  ))}
                </Box>
              </SortableList>
            </Box>
          </Stack>
        )}
      </Stack>

      <Stack spacing={1.5}>
        <Stack spacing={0.5}>
          <Typography variant="title-s">Non-grantor trusts</Typography>
          <Typography variant="body-xs" sx={{ color: COLORS.TEXT.PRIMARY }}>
            Specify which entity pays the taxes on investment income generated
            by non-grantor trusts.
          </Typography>
        </Stack>
        <FormAwareRadioGroup<GrowthProfileModalFormShape>
          control={control}
          fieldName="taxDrag.nonGrantorPaymentMethod"
          size="small"
          row={false}
          columnSpacing={1}
          options={[
            {
              label:
                'Each non-grantor trust pays taxes on its own investment income',
              value: IncomeTaxPaymentConfigNonGrantorPaymentMethod.Self,
            },
            {
              label:
                'Follow the payment order set for entities taxed to the grantor',
              value:
                IncomeTaxPaymentConfigNonGrantorPaymentMethod.FollowGrantor,
            },
          ]}
        />
      </Stack>
    </Stack>
  );
}
